import type {
  InvoiceReportResponse,
  InvoiceRow,
  ProgressBilling,
} from '../interfaces/InvoiceService';

/**
 * Authoritative wire shape returned by `/reports/consumer/invoices.json`.
 * Source of truth: `/Users/chris/Sera/api/app/datatables/consumer/invoices_datatable.rb#data`.
 *
 * The base row is the flat DataTables format with dollar-string displays for
 * money fields. When the tenant has Progress Billing (SPB) enabled and the
 * invoice belongs to a progress contract, the row is augmented with the
 * snake_case SPB block from `_progress_billing.json.jbuilder` and
 * `Consumer::InvoicesDatatable#progress_billing_for`. See
 * `.planning/sketches/021-invoice-list/README.md` (lines 44-66) for the full
 * wire/domain contract.
 */
export interface InvoiceReportWire {
  /** Invoice id. The datatable typically ships a number but stringy ids are
   * tolerated as a defensive read (mirrors appointmentRow). */
  id: number | string;
  public_id?: string;
  /** Display date string from the wire, e.g. "May 20, 2026". Not ISO. */
  date?: string;
  job_id?: string;
  job_public_id?: string | null;
  /** Dollar string ("$340.00") or sentinel "--" when absent. */
  total?: string;
  balance?: string;

  // SPB (Progress Billing) augmentation. ENVELOPE CONFIRMED NESTED (D-03, A1/OQ1):
  // the api merges the SPB payload as a NESTED object via
  // `row.merge(progress_billing: spb)` in
  // `../api/app/datatables/consumer/invoices_datatable.rb:44`, and
  // `InvoiceService.fetchInvoiceReport` passes the raw HTTP `data` straight
  // to `adaptInvoiceReportResponse` with ZERO reshaping
  // (InvoiceService.ts) — so the adapter sees `wire.progress_billing.*`
  // exactly as emitted. The flat scalar keys are built by
  // `build_progress_billing_payload` (invoices_datatable.rb:86-101) and asserted
  // nested in `../api/spec/datatables/consumer/invoices_datatable_spec.rb`.
  // Non-SPB list rows OMIT the key entirely; the detail payload sends it as null.
  // The optional-chained `progress_billing?.is_progress_billing` gate is safe for
  // both. NOTE: `current_draw_amount_cents` is the REMAINING due amount despite
  // the name (D-04) and maps to `currentDraw.remainingCents`. The list never
  // sends `installments[]`, `next_draw_after_current`, or `payment_status`
  // (detail-only). Consumed by buildProgressBilling below.
  progress_billing?: {
    is_progress_billing: boolean;
    // Typed as a raw string (NOT the domain union) so the adapter can validate
    // an out-of-taxonomy code at runtime rather than trusting the wire (WR-01).
    payment_status_code: string;
    current_draw_label?: string | null;
    /** ⚠ REMAINING due, despite the name → currentDraw.remainingCents (D-04). */
    current_draw_amount_cents?: number;
    current_draw_due_date?: string | null;
    is_currently_due?: boolean;
    is_past_due?: boolean;
    past_due_amount_cents?: number;
    contract_total_cents?: number;
    installment_position?: number | null;
    installment_count?: number;
  } | null;
}

/**
 * DataTables envelope shape. recordsTotal/recordsFiltered/draw are all
 * optional in this seam (defensive against test fixtures and API drift);
 * adaptInvoiceReportResponse defaults each.
 */
export interface InvoiceReportResponseWire {
  data: InvoiceReportWire[];
  recordsTotal?: number;
  recordsFiltered?: number;
  draw?: number;
}

/**
 * Parse a wire dollar string ("$340.00", "$1,200.50") into integer cents.
 * Returns 0 for undefined, empty string, or the "--" sentinel so the domain
 * never carries NaN cents (sketch 021 derives status from cents math; treating
 * absent values as zero is the documented behavior). Uses Math.round to avoid
 * float drift from parseFloat * 100.
 */
function parseDollarsToCents(raw: string | undefined): number {
  if (raw === undefined || raw === null) return 0;
  if (raw === '' || raw === '--') return 0;
  // WR-08: broaden the strip to all non-numeric/decimal/sign chars so trailing
  // unit strings ("$1,200.50 USD") or extra whitespace don't slip through and
  // get truncated mid-parse. Also distinguish "empty after strip" from
  // "parsed as 0" so we don't silently swallow `"abc"` as zero.
  const stripped = raw.replace(/[^0-9.-]/g, '');
  if (!stripped) return 0;
  const n = parseFloat(stripped);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Coerce a wire id (number or string-numeric) to a number. Mirrors
 * appointmentRow's helper; returns 0 on NaN as a defensive fallback.
 */
function normalizeId(raw: number | string | undefined): number {
  if (typeof raw === 'number') return raw;
  if (raw === undefined || raw === null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Normalize a wire display field to a UI-safe value. The datatable emits the
 * literal "--" sentinel for an absent value (a null `job_id` via `display_value`,
 * a missing date via `format_date`). The card hides a meta chip only when its
 * value is empty, so collapse "--" (and undefined/null/empty) to '' here — the
 * same "--" sentinel convention `parseDollarsToCents` already honors. A real
 * value passes through verbatim (the wire ships no ISO date for this endpoint).
 */
function normalizeDisplayField(raw: string | undefined | null): string {
  if (raw === undefined || raw === null) return '';
  if (raw === '--') return '';
  return raw;
}

/** The nested SPB block as it arrives on the wire (non-null narrowed). */
type WireProgressBilling = NonNullable<InvoiceReportWire['progress_billing']>;

/**
 * The closed set of SPB payment-status codes the domain understands, mirroring
 * `ProgressBilling::InvoicePresenter::PAYMENT_STATUSES` on the api side
 * (../api/app/services/progress_billing/invoice_presenter.rb). WR-01: the
 * adapter validates the wire code against this set rather than copying it
 * verbatim — an out-of-taxonomy code is treated as non-SPB so a downstream card
 * can never mislabel an unknown status (e.g. fall through to "Paid in full").
 */
const KNOWN_PAYMENT_STATUSES: ReadonlySet<ProgressBilling['paymentStatus']> =
  new Set<ProgressBilling['paymentStatus']>([
    'voided',
    'current_draw_due',
    'past_due',
    'no_payment_due',
    'paid_in_full',
  ]);

function isKnownPaymentStatus(
  code: unknown,
): code is ProgressBilling['paymentStatus'] {
  return (
    typeof code === 'string' &&
    KNOWN_PAYMENT_STATUSES.has(code as ProgressBilling['paymentStatus'])
  );
}

/**
 * Build the domain `currentDraw` sub-block from the flat wire triplet, or
 * undefined when no draw is present (paid_in_full sends a null label).
 * `current_draw_amount_cents` is the REMAINING due amount despite the name
 * (D-04) → `remainingCents`. `amountCents` is detail-only on the list, so we
 * mirror the remaining figure into both so downstream cents math has a value
 * without a detail fetch.
 */
function buildCurrentDraw(
  spb: WireProgressBilling,
): ProgressBilling['currentDraw'] | undefined {
  if (spb.current_draw_label == null) return undefined;
  return {
    label: spb.current_draw_label,
    amountCents: spb.current_draw_amount_cents ?? 0,
    remainingCents: spb.current_draw_amount_cents ?? 0,
    dueDate: spb.current_draw_due_date ?? '',
    isDue: spb.is_currently_due ?? false,
  };
}

/**
 * Build the camelCase ProgressBilling domain block from the wire's FLAT
 * snake_case keys, nested under `wire.progress_billing`
 * (`../api/app/datatables/consumer/invoices_datatable.rb:44`).
 *
 * Returns undefined unless the row carries `progress_billing.is_progress_billing
 * === true` AND a `payment_status_code`. Non-SPB list rows omit the key entirely
 * and the detail payload sends it as null; the optional-chained gate handles
 * both → regular (non-SPB) treatment, including a cancelled PB plan (D-08).
 *
 * The list endpoint never sends `installments[]` or `next_draw_after_current`
 * (detail-only), so the domain `installments`/`nextDrawAfterCurrent` fields
 * stay unset here.
 */
function buildProgressBilling(
  wire: InvoiceReportWire,
): ProgressBilling | undefined {
  const spb = wire.progress_billing;
  if (!spb || !spb.is_progress_billing || !spb.payment_status_code) {
    return undefined;
  }
  // WR-01: reject an out-of-taxonomy payment_status_code (don't trust the wire
  // string verbatim). An unknown code falls through to non-SPB treatment so the
  // card derives status from cents math instead of mislabeling it.
  if (!isKnownPaymentStatus(spb.payment_status_code)) {
    return undefined;
  }

  const out: ProgressBilling = {
    paymentStatus: spb.payment_status_code,
    contractTotalCents: spb.contract_total_cents ?? 0,
  };

  const currentDraw = buildCurrentDraw(spb);
  if (currentDraw) out.currentDraw = currentDraw;
  if (spb.past_due_amount_cents !== undefined) {
    out.pastDueAmountCents = spb.past_due_amount_cents;
  }
  if (spb.installment_position != null) {
    out.installmentPosition = spb.installment_position;
  }
  if (spb.installment_count !== undefined) {
    out.installmentCount = spb.installment_count;
  }

  return out;
}

/**
 * Map one wire row to the cents-based domain InvoiceRow consumed by the UI.
 *
 * Money fields (`total`, `balance`) are parsed from the wire's dollar-string
 * display into integer cents. A missing `total` or `balance` defaults to 0
 * (sketch 021 derives status from cents math; treat absent values as zero
 * rather than NaN). `id` is coerced to a number; `job_id` and `date` pass
 * through `normalizeDisplayField`, which collapses the api "--" sentinel (and
 * undefined/null) to '' so the card can hide an empty meta chip rather than
 * render "Job #--" / a stray "--" date (the wire ships no ISO date here).
 *
 * The optional `progressBilling` key is omitted entirely (not set to
 * undefined) when the wire row is not a progress-billing invoice, so
 * consumers can use `'progressBilling' in row` checks.
 */
export function adaptInvoiceRow(wire: InvoiceReportWire): InvoiceRow {
  const progressBilling = buildProgressBilling(wire);
  const row: InvoiceRow = {
    id: normalizeId(wire.id),
    jobId: normalizeDisplayField(wire.job_id),
    dateDisplay: normalizeDisplayField(wire.date),
    totalCents: parseDollarsToCents(wire.total),
    balanceCents: parseDollarsToCents(wire.balance),
  };
  if (progressBilling) {
    row.progressBilling = progressBilling;
  }
  return row;
}

/**
 * Map the full DataTables envelope. recordsTotal/recordsFiltered default to
 * data.length and draw defaults to 0 when omitted by the server (defensive
 * against test fixtures and API drift; mirrors the appointmentRow envelope
 * tolerance).
 */
export function adaptInvoiceReportResponse(
  wire: InvoiceReportResponseWire,
): InvoiceReportResponse {
  const data = (wire.data ?? []).map(adaptInvoiceRow);
  return {
    data,
    recordsTotal: wire.recordsTotal ?? data.length,
    recordsFiltered: wire.recordsFiltered ?? data.length,
    draw: wire.draw ?? 0,
  };
}
